import { WagakoApiBase } from './wagakoApiBase';

import type {
  NurseryApiEntityCreate,
  NurseryApiEntityUpdate,
  NurseryApiEntityDelete,
  NurseryApiStorageGet,
  NurseryApiSpecialNurseryGet,
  NurseryApiSpecialNurseryListGet,
  NurseryApiSpecialChildUpdate,
  NurseryApiSpecialPaymentCardsCreate,
  NurseryApiSpecialPaymentCardsGet,
  NurseryApiSpecialPhotoPayCreate,
  NurseryApiSpecialPhotoPayCardCreate,
  NurseryApiSpecialGetPhotoOrdered,
  NurseryApiStorageImage,
  NurseryApiSpecialPhotoPayCancel,
  NurseryApiSpecialPhotoPayCardCancel,
  NurseryApiStorageDelete,
  NurseryApiStorageCreate,
} from './wagakoNurseryApiTypes';
import type { WagakoNurseryIApi } from './wagakoNurseryIApi';
import type { EntityPub } from '@/domain/entityPub';

import { apiWagakoNurseryUrl } from '@/constants/env';
import { S3NetworkError } from '@/errors/S3NetworkError';
import { Api403ForbiddenError } from '@/errors/api403ForbiddenError';
import { ApiResponseError } from '@/errors/apiResponseError';

/**
 * WagakoNurseryApi
 */
export class WagakoNurseryApi extends WagakoApiBase implements WagakoNurseryIApi {
  constructor() {
    super();
    this.childId = '';
    this.parentId = '';
  }

  /**
   * API URL.
   */
  protected get apiUrl(): string {
    return apiWagakoNurseryUrl;
  }

  /**
   * 子どもID
   */
  protected childId: string;

  /**
   * 親ID
   */
  protected parentId: string;

  /**
   * 子どもIDをセット
   * @param childId 子どもID
   */
  public setChildId(childId: string) {
    this.childId = childId;
  }

  /**
   * 親IDをセット
   * @param parentId 親ID
   */
  public setParentId(parentId: string) {
    this.parentId = parentId;
  }

  /**
   * 施設を取得
   * @param nurseryId 施設ID
   * @returns レスポンス
   */
  public async getNursery(nurseryId: string) {
    const response = await this.callApi<
      NurseryApiSpecialNurseryGet.Request.Type,
      NurseryApiSpecialNurseryGet.Response.Type
    >({
      path: '/app/special/nursery/{nurseryId}',
      pathParameters: {
        nurseryId,
      },
      method: 'get',
    });
    return response;
  }

  /**
   * 施設一覧を取得
   * @returns レスポンス
   */
  public async getNurseryList() {
    const response = await this.callApi<
      NurseryApiSpecialNurseryListGet.Request.Type,
      NurseryApiSpecialNurseryListGet.Response.Type
    >({
      path: '/app/special/nursery',
      method: 'get',
    });
    return response;
  }

  /**
   * 送る
   * @param input 入力
   * @returns 結果
   */
  public async send<PvtProps, PvtJson, PubProps, PubJson>(
    input: EntityPub<PvtProps, PvtJson, PubProps, PubJson>,
  ): Promise<{
    /** メッセージ */
    message: string,
  }> {
    const data = input.toCreateEntityInput();
    const { replyId } = input.pub;
    const sendId = `${this.parentId}_${this.childId}`;
    const response = await this.callApi<
      NurseryApiEntityCreate.Request.Type,
      NurseryApiEntityCreate.Response.Type
    >({
      path: '/app/entity/{entityName}/{toId}/{fromId}',
      pathParameters: {
        entityName: input.entityName,
        toId: replyId,
        fromId: sendId,
      },
      method: 'put',
      body: { data },
    });
    return response;
  }

  /**
   * 取り除く
   * @param input 入力
   * @returns 結果
   */
  public async remove<PvtProps, PvtJson, PubProps, PubJson>(
    input: EntityPub<PvtProps, PvtJson, PubProps, PubJson>,
  ): Promise<{
    /** メッセージ */
    message: string,
  }> {
    const data = input.toDeleteEntityInput();
    const { sendId, replyId } = input.pub;
    const response = await this.callApi<
      NurseryApiEntityDelete.Request.Type,
      NurseryApiEntityDelete.Response.Type
    >({
      path: '/app/entity/{entityName}/{toId}/{fromId}',
      pathParameters: {
        entityName: input.entityName,
        toId: replyId,
        fromId: sendId,
      },
      method: 'delete',
      body: { data },
    });
    return response;
  }

  /**
   * 送信先のEntityのpubを更新します.
   * @param input 入力.
   * @returns message.
   */
  public async reply<PvtProps, PvtJson, PubProps, PubJson>(
    input: EntityPub<PvtProps, PvtJson, PubProps, PubJson>,
  ): Promise<{
    /** メッセージ */
    message: string,
  }> {
    const data = input.toUpdateEntityInput();
    const { sendId, replyId } = input.pub;
    const response = await this.callApi<
      NurseryApiEntityUpdate.Request.Type,
      NurseryApiEntityUpdate.Response.Type
    >({
      path: '/app/entity/{entityName}/{toId}/{fromId}',
      pathParameters: {
        entityName: input.entityName,
        toId: sendId,
        fromId: replyId,
      },
      method: 'post',
      body: { data },
    });
    return response;
  }

  /**
   * S3にファイルを保存します
   * @param s3Path S3パス
   * @param nurseryId 施設ID
   * @param body ボディ
   * @returns 結果
   */
  public async saveFileToS3(s3Path: string, nurseryId: string, body: Blob) {
    const { entityName, entityKey, name } = this.splitS3Path(s3Path);
    const response = await this.callApi<
      NurseryApiStorageCreate.Request.Type,
      NurseryApiStorageCreate.Response.Type
    >({
      path: '/app/storage/{entityName}/{entityKey}/{name}/{toId}',
      pathParameters: {
        entityName,
        entityKey,
        name,
        toId: nurseryId,
      },
      method: 'put',
    });
    if (response.message !== 'OK' || !response.data) {
      throw new S3NetworkError(`Error in WagakoNurseryApi/storage put: ${response.message}`);
    }
    await this.uploadS3(response.data.url, response.data.fields, body);
  }

  /**
   * S3にファイルをアップロードします
   * @param url 署名付きS3POSTのURL
   * @param fields 署名付きS3POSTのフィールド
   * @param body ボディ
   * @returns 結果
   */
  private async uploadS3(
    url: string,
    fields: { [key: string]: string },
    body: Blob,
  ): Promise<void> {
    const result = await this.apiPostFileToS3(url, fields, body);
    if (result.status === 503) {
      // 503 Slow Down リトライ
      await (new Promise<void>((resolve) => { setTimeout(resolve, 1000); }));
      await this.uploadS3(url, fields, body);
    }
    if (result.status > 299) {
      throw new S3NetworkError(`Error in Upload S3: ${result.msg}`);
    }
  }

  /**
   * S3からファイルを削除します
   * @param s3Path S3パス
   * @param nurseryId 施設ID
   */
  public async deleteFileFromS3(
    s3Path: string,
    nurseryId: string,
  ) {
    const { entityName, entityKey, name } = this.splitS3Path(s3Path);
    const response = await this.callApi<
      NurseryApiStorageDelete.Request.Type,
      NurseryApiStorageDelete.Response.Type
    >({
      path: '/app/storage/{entityName}/{entityKey}/{name}/{toId}',
      pathParameters: {
        entityName,
        entityKey,
        name,
        toId: nurseryId,
      },
      method: 'delete',
    });
    return response;
  }

  /**
   * S3からファイル読み込み
   * @param s3Path S3パス
   * @param nurseryId 施設ID
   * @returns 結果
   */
  public async loadFileFromS3(
    s3Path: string,
    nurseryId: string,
  ): Promise<{
    /** メッセージ */
    message: string,
    /** データ */
    data: ArrayBuffer,
  }> {
    const { entityName, entityKey, name } = this.splitS3Path(s3Path);
    const data = await this.callApi<
      NurseryApiStorageGet.Request.Type,
      NurseryApiStorageGet.Response.Type
    >({
      path: '/app/storage/{entityName}/{entityKey}/{name}/{toId}',
      pathParameters: {
        entityName,
        entityKey,
        name,
        toId: nurseryId,
      },
      method: 'get',
    });
    if (data.statusCode === 200) {
      const res = await this.fetchS3(data.data.url);
      if (res.data !== undefined) {
        return {
          message: res.message, data: res.data,
        };
      }
      throw new ApiResponseError(`loadFileFromS3-GetS3File:${res.status}:${res.message}:${s3Path}:${nurseryId}`);
    } else if (data.statusCode === 403) {
      throw new Api403ForbiddenError(`loadFileFromS3:${data.statusCode}:${data.message}:${s3Path}:${nurseryId}`);
    }
    throw new ApiResponseError(`loadFileFromS3:???:???:${s3Path}:${nurseryId}`);
  }

  /**
   * S3からファイル読み込み
   * @param s3Path S3パス
   * @param nurseryId 施設ID
   * @returns 結果
   */
  public async fetchS3(
    url: string,
  ): Promise<{
    /** メッセージ */
    message: string,
    /** データ */
    data: ArrayBuffer | undefined,
    /** ステータス */
    status: number,
  }> {
    const res = await this.apiGetFileFromS3(url);
    if (res.status === 200) {
      const d = res.content as ArrayBuffer;
      return { message: 'OK', data: d, status: res.status };
    }
    const d = res.content as ArrayBuffer;
    const message = new TextDecoder().decode(new Uint8Array(d));
    return { message, data: undefined, status: res.status };
  }

  /**
   * S3からファイル読み込み
   * @param s3Path S3パス
   * @param nurseryId 施設ID
   * @returns 結果
   */
  public async getFileBase64FromS3(
    s3Path: string,
    nurseryId: string,
  ): Promise<{
    /** メッセージ */
    message: string,
    /** Blob */
    blob: Blob | undefined,
  }> {
    const { message, data } = await this.loadFileFromS3(s3Path, nurseryId);
    const blob = new Blob([data], { type: 'application/octet-stream' });
    return { message, blob };
  }

  /**
   * S3から画像読み込み
   * @param s3Path S3パス
   * @param nurseryId 施設ID
   * @returns 結果
   */
  public async getImageBase64FromS3(
    s3Path: string,
    nurseryId: string,
  ): Promise<{
    /** メッセージ */
    message: string,
    /** Blob */
    blob: Blob | undefined,
  }> {
    const { entityName, entityKey, name } = this.splitS3Path(s3Path);
    const response = await this.callImageApi<
      NurseryApiStorageImage.Request.Type
    >(
      {
        path: '/app/storage-image/{entityName}/{entityKey}/{name}/{toId}',
        pathParameters: {
          entityName,
          entityKey,
          name,
          toId: nurseryId,
        },
        method: 'get',
      },
      name,
    );
    return response;
  }

  /**
   * getOrderedPhotoUrlFromS3
   * @param nurseryId 施設ID
   * @param orderDate 注文日
   * @param salesGroupId 販売グループID
   * @param date 日
   * @returns 結果
   */
  public async getOrderedPhotoUrlFromS3(
    nurseryId: string,
    orderDate: string,
    salesGroupId: string,
    date: string,
  ): Promise<{
    /** メッセージ */
    message: string,
    /** アイテムリスト */
    items?: NurseryApiSpecialGetPhotoOrdered.PhotoUrlType[],
  }> {
    const response = await this.callApi<
      NurseryApiSpecialGetPhotoOrdered.Request.Type,
      NurseryApiSpecialGetPhotoOrdered.Response.Type
    >({
      path: '/app/special/photo/ordered/{nurseryId}/{orderDate}/{salesGroupId}/{date}',
      pathParameters: {
        nurseryId,
        orderDate,
        salesGroupId,
        date,
      },
      method: 'get',
    });
    if (response.message === 'OK' && response.data) {
      return {
        message: response.message,
        items: response.data.photos,
      };
    }
    return { message: response.message };
  }

  /**
   * 子ども更新
   * @param nurseryId 施設ID
   * @param data 日時
   * @returns 結果
   */
  public async updateChild(
    nurseryId: string,
    data: NurseryApiSpecialChildUpdate.Request.BodyDataType,
  ) {
    const response = await this.callApi<
      NurseryApiSpecialChildUpdate.Request.Type,
      NurseryApiSpecialChildUpdate.Response.Type
    >({
      path: '/app/special/child/{nurseryId}',
      pathParameters: {
        nurseryId,
      },
      method: 'post',
      body: { data },
    });
    return response;
  }

  /**
   * クレジットカード登録
   * @param nurseryId 施設ID
   * @param data データ
   * @returns レスポンス
   */
  public async createCreditCard(
    nurseryId: string,
    data: NurseryApiSpecialPaymentCardsCreate.Request.BodyDataType,
  ) {
    const response = await this.callApi<
      NurseryApiSpecialPaymentCardsCreate.Request.Type,
      NurseryApiSpecialPaymentCardsCreate.Response.Type
    >({
      path: '/app/special/payment/card/{nurseryId}',
      pathParameters: {
        nurseryId,
      },
      method: 'post',
      body: { data },
    });
    return response;
  }

  /**
   * 登録済みクレジットカード一覧を取得します
   * @param nurseryId 施設ID
   * @returns レスポンス
   */
  public async getCreditCardList(nurseryId: string) {
    const response = await this.callApi<
      NurseryApiSpecialPaymentCardsGet.Request.Type,
      NurseryApiSpecialPaymentCardsGet.Response.Type
    >({
      path: '/app/special/payment/card/{nurseryId}',
      pathParameters: {
        nurseryId,
      },
      method: 'get',
    });
    return response;
  }

  /**
   * 施設支払いで写真購入
   * @param nurseryId 施設ID
   * @param data データ
   * @returns レスポンス
   */
  public async photoPayByNursery(
    nurseryId: string,
    data: NurseryApiSpecialPhotoPayCreate.Request.BodyDataType,
  ) {
    const response = await this.callApi<
      NurseryApiSpecialPhotoPayCreate.Request.Type,
      NurseryApiSpecialPhotoPayCreate.Response.Type
    >({
      path: '/app/special/photo/pay/{nurseryId}',
      pathParameters: {
        nurseryId,
      },
      method: 'post',
      body: { data },
    });
    return response;
  }

  /**
   * クレジットカードで購入.
   * @param nurseryId 施設ID
   * @param data データ
   * @returns レスポンス
   */
  public async photoPayByCard(
    nurseryId: string,
    data: NurseryApiSpecialPhotoPayCardCreate.Request.BodyDataType,
  ) {
    const response = await this.callApi<
      NurseryApiSpecialPhotoPayCardCreate.Request.Type,
      NurseryApiSpecialPhotoPayCardCreate.Response.Type
    >({
      path: '/app/special/photo/pay-card/{nurseryId}',
      pathParameters: {
        nurseryId,
      },
      method: 'post',
      body: { data },
    });
    return response;
  }

  /**
   * 写真購入キャンセル
   * @param nurseryId 施設ID
   * @param data データ
   * @returns レスポンス
   */
  public async cancelPhotoOrder(
    nurseryId: string,
    data: NurseryApiSpecialPhotoPayCancel.Request.BodyDataType,
  ) {
    const response = await this.callApi<
      NurseryApiSpecialPhotoPayCancel.Request.Type,
      NurseryApiSpecialPhotoPayCancel.Response.Type
    >({
      path: '/app/special/photo/pay-cancel/{nurseryId}',
      pathParameters: {
        nurseryId,
      },
      method: 'post',
      body: { data },
    });
    return response;
  }

  /**
   * クレジットカード写真購入キャンセル
   * @param nurseryId 施設ID
   * @param data データ
   * @returns レスポンス
   */
  public async cancelCreditCardPhotoOrder(
    nurseryId: string,
    data: NurseryApiSpecialPhotoPayCardCancel.Request.BodyDataType,
  ) {
    const response = await this.callApi<
      NurseryApiSpecialPhotoPayCardCancel.Request.Type,
      NurseryApiSpecialPhotoPayCardCancel.Response.Type
    >({
      path: '/app/special/photo/pay-cancel-card/{nurseryId}',
      pathParameters: {
        nurseryId,
      },
      method: 'post',
      body: { data },
    });
    return response;
  }
}
